// v236 admin page for the in-app API playground (item 55 Phase D /
// item 64 in the recommended delivery order of PRODUCT-ROADMAP.md).
//
// Phase A landed `api/openapi.yaml` (auto-generated from the chi
// route registrations). Phase B rendered it as the public Redoc
// reference at docs.orbitalreg.io/reference/api-explorer. Phase C
// shipped the curl/Python/Go/JS code samples + getting-started
// tutorial. This page closes Phase D: an opt-in, in-app browser
// + try-it surface for operators who want to fire one-off API
// calls against the live deployment without leaving the admin
// shell.
//
// The toggle is *opt-in* (default OFF) because flipping it ON in
// production opens a self-DDoS surface — an admin can lean on the
// "Send" button against a heavy /api/v1/search and burn the
// instance. The page therefore renders the toggle UI even when the
// playground is disabled, but the operations list + try-it form
// stay hidden until an admin opts in. All requests use the same
// session cookie the admin already has, so no separate token
// management.

import { Fragment, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Code2,
  Download,
  PlayCircle,
  RefreshCw,
  Search,
} from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { CopyIconButton } from '../../components/ui/CopyIconButton';
import {
  useAPIPlaygroundSettings,
  useAPIPlaygroundOperations,
  updateAPIPlaygroundSettings,
  type APIOperation,
  type APIOperationParameter,
} from '../../api/apiPlayground';

const METHOD_TONE: Record<
  string,
  'accent' | 'success' | 'nebula' | 'warning' | 'danger' | 'neutral'
> = {
  GET: 'accent',
  HEAD: 'neutral',
  POST: 'success',
  PUT: 'nebula',
  PATCH: 'nebula',
  DELETE: 'danger',
};

interface TryFormState {
  pathParams: Record<string, string>;
  queryParams: Record<string, string>;
  headerParams: Record<string, string>;
  body: string;
}

interface TryResult {
  status: number;
  statusText: string;
  contentType: string;
  body: string;
  durationMs: number;
}

function buildEmptyForm(op: APIOperation): TryFormState {
  const state: TryFormState = {
    pathParams: {},
    queryParams: {},
    headerParams: {},
    body: '',
  };
  for (const p of op.parameters ?? []) {
    if (p.in === 'path') state.pathParams[p.name] = '';
    if (p.in === 'query') state.queryParams[p.name] = '';
    if (p.in === 'header') state.headerParams[p.name] = '';
  }
  return state;
}

function substitutePath(path: string, params: Record<string, string>): string {
  return path.replace(/\{([^}]+)\}/g, (_, key) =>
    encodeURIComponent(params[key] ?? `{${key}}`),
  );
}

function appendQuery(url: string, params: Record<string, string>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== '') usp.append(k, v);
  }
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}

function asTone(method: string) {
  return METHOD_TONE[method] ?? 'neutral';
}

function resultStatusTone(status: number): 'success' | 'danger' | 'warning' {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 400) return 'danger';
  return 'warning';
}

function paramKey(p: APIOperationParameter) {
  return `${p.in}:${p.name}`;
}

interface OpRowProps {
  readonly op: APIOperation;
}

function OperationRow({ op }: OpRowProps) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<TryFormState>(() => buildEmptyForm(op));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TryResult | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function setPath(name: string, value: string) {
    setForm((f) => ({ ...f, pathParams: { ...f.pathParams, [name]: value } }));
  }
  function setQuery(name: string, value: string) {
    setForm((f) => ({ ...f, queryParams: { ...f.queryParams, [name]: value } }));
  }
  function setHeader(name: string, value: string) {
    setForm((f) => ({ ...f, headerParams: { ...f.headerParams, [name]: value } }));
  }

  async function send() {
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const url = appendQuery(
        substitutePath(op.path, form.pathParams),
        form.queryParams,
      );
      const headers: Record<string, string> = { Accept: 'application/json' };
      for (const [k, v] of Object.entries(form.headerParams)) {
        if (v !== '') headers[k] = v;
      }
      let body: string | undefined;
      if (op.has_body && form.body !== '') {
        const ct = op.body_type || 'application/json';
        headers['Content-Type'] = ct;
        body = form.body;
      }
      const t0 = performance.now();
      const res = await fetch(url, {
        method: op.method,
        credentials: 'include',
        headers,
        body,
      });
      const text = await res.text();
      setResult({
        status: res.status,
        statusText: res.statusText,
        contentType: res.headers.get('Content-Type') ?? '',
        body: text,
        durationMs: Math.round(performance.now() - t0),
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const params = op.parameters ?? [];
  const pathParams = params.filter((p) => p.in === 'path');
  const queryParams = params.filter((p) => p.in === 'query');
  const headerParams = params.filter((p) => p.in === 'header');

  return (
    <div className="border border-border rounded-md bg-bg-elevated/30">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-bg-hover/40 transition-colors cursor-pointer focus:outline-none"
      >
        <Badge tone={asTone(op.method)} className="w-16 justify-center font-mono">
          {op.method}
        </Badge>
        <code className="text-sm font-mono text-fg truncate" title={op.path}>{op.path}</code>
        <span onClick={(e) => e.stopPropagation()}>
          <CopyIconButton value={op.path} label="Copy full path" />
        </span>
        {op.summary && (
          <span className="text-sm text-fg-muted truncate ml-auto pl-3" title={op.summary}>
            {op.summary}
          </span>
        )}
        {op.deprecated && <Badge tone="warning">deprecated</Badge>}
      </div>

      {open && (
        <div className="border-t border-border px-4 py-3 space-y-4">
          {op.description && (
            <p className="text-sm text-fg-muted leading-relaxed">{op.description}</p>
          )}

          {params.length > 0 && (
            <div className="space-y-2">
              <p className="orb-section-title text-fg-subtle">Parameters</p>
              <div className="space-y-2">
                {pathParams.length > 0 && (
                  <ParamGroup
                    label="Path"
                    items={pathParams}
                    values={form.pathParams}
                    onChange={setPath}
                  />
                )}
                {queryParams.length > 0 && (
                  <ParamGroup
                    label="Query"
                    items={queryParams}
                    values={form.queryParams}
                    onChange={setQuery}
                  />
                )}
                {headerParams.length > 0 && (
                  <ParamGroup
                    label="Header"
                    items={headerParams}
                    values={form.headerParams}
                    onChange={setHeader}
                  />
                )}
              </div>
            </div>
          )}

          {op.has_body && (
            <div className="space-y-1.5">
              <p className="orb-section-title text-fg-subtle">
                Request body{' '}
                {op.body_type && (
                  <span className="text-fg-muted font-normal">({op.body_type})</span>
                )}
              </p>
              {op.body_description && (
                <p className="text-xs text-fg-muted">{op.body_description}</p>
              )}
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={6}
                spellCheck={false}
                placeholder={
                  op.body_type === 'application/json'
                    ? '{\n  \n}'
                    : '<request body>'
                }
                className="w-full font-mono text-xs p-2.5 rounded-md bg-bg-inset border border-border focus:border-accent focus:outline-none"
              />
            </div>
          )}

          {(op.responses?.length ?? 0) > 0 && (
            <div className="space-y-1.5">
              <p className="orb-section-title text-fg-subtle">Responses</p>
              <div className="flex flex-wrap gap-2">
                {op.responses!.map((r) => (
                  <span
                    key={r.status}
                    className="text-xs text-fg-muted border border-border rounded px-2 py-0.5"
                  >
                    <span className="font-mono text-fg">{r.status}</span>
                    {r.description && <span className="ml-1.5">{r.description}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button onClick={send} disabled={busy} variant="primary" size="sm">
              <PlayCircle className="h-4 w-4" />
              {busy ? 'Sending…' : 'Try it'}
            </Button>
            {result && (
              <span className="text-xs text-fg-muted">
                {result.durationMs} ms · {result.contentType || 'no content-type'}
              </span>
            )}
          </div>

          {err && (
            <div className="text-sm text-danger border border-danger/40 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          {result && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Badge
                  tone={resultStatusTone(result.status)}
                  className="font-mono"
                >
                  {result.status} {result.statusText}
                </Badge>
              </div>
              <pre className="text-xs font-mono p-3 rounded-md bg-bg-inset border border-border overflow-x-auto max-h-80 whitespace-pre-wrap break-words">
                {prettifyBody(result.body, result.contentType)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function prettifyBody(body: string, ct: string): string {
  if (!body) return '(empty body)';
  if (ct.includes('json')) {
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  }
  return body;
}

interface ParamGroupProps {
  readonly label: string;
  readonly items: APIOperationParameter[];
  readonly values: Record<string, string>;
  readonly onChange: (name: string, value: string) => void;
}

function ParamGroup({ label, items, values, onChange }: ParamGroupProps) {
  return (
    <div>
      <p className="text-xs text-fg-subtle uppercase tracking-wide mb-1">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-x-3 gap-y-1.5">
        {items.map((p) => (
          <Fragment key={paramKey(p)}>
            <label className="text-xs text-fg-muted self-center">
              <code className="font-mono text-fg">{p.name}</code>
              {p.required && <span className="text-danger ml-1">*</span>}
              {p.type && (
                <span className="text-fg-subtle ml-1.5">({p.type})</span>
              )}
            </label>
            <Input
              value={values[p.name] ?? ''}
              onChange={(e) => onChange(p.name, e.target.value)}
              placeholder={p.description || ''}
              className="text-sm"
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

export function AdminApiReference() {
  const settings = useAPIPlaygroundSettings();
  const enabled = settings.data?.enabled ?? false;
  const operations = useAPIPlaygroundOperations(enabled);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [tagFilter, setTagFilter] = useState<string>('');

  async function setEnabled(next: boolean) {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await updateAPIPlaygroundSettings({ enabled: next });
      setOk(next ? 'Playground enabled.' : 'Playground disabled.');
      await Promise.all([settings.mutate(), operations.mutate()]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const ops = operations.data?.operations ?? [];
  const tags = useMemo(() => {
    const seen = new Set<string>();
    for (const op of ops) if (op.tag) seen.add(op.tag);
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [ops]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return ops.filter((op) => {
      if (tagFilter && op.tag !== tagFilter) return false;
      if (!needle) return true;
      return (
        op.path.toLowerCase().includes(needle) ||
        (op.summary ?? '').toLowerCase().includes(needle) ||
        (op.operation_id ?? '').toLowerCase().includes(needle)
      );
    });
  }, [ops, filter, tagFilter]);

  // Group filtered operations by tag for the rendered list.
  const grouped = useMemo(() => {
    const out = new Map<string, APIOperation[]>();
    for (const op of filtered) {
      const key = op.tag || 'Other';
      const list = out.get(key) ?? [];
      list.push(op);
      out.set(key, list);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <AdminShell
      title={
        <span className="flex items-center gap-2">
          <Code2 className="h-5 w-5 text-nebula" />
          API Playground
        </span>
      }
      description="In-app browser for the live REST surface of this deployment, with a try-it form that fires fetch() against your current admin session. Disabled by default — turn it on once you're comfortable with the self-DDoS risk in production."
    >
      {(err || ok) && (
        <Card className={err ? 'mb-4 border-danger/40' : 'mb-4 border-success/40'}>
          <CardBody>
            <p className={err ? 'text-sm text-danger' : 'text-sm text-success'}>
              {err || ok}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Card 1 — Master toggle */}
      <Card className="mb-6">
        <CardHeader
          title="Master toggle"
          description="Default OFF. When ON, /admin/api-reference renders the operation browser + try-it forms. Operators can still read the toggle state when it's off."
        />
        <CardBody>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-sm text-fg">
                Playground is{' '}
                <strong className={enabled ? 'text-success' : 'text-fg-muted'}>
                  {enabled ? 'enabled' : 'disabled'}
                </strong>
                {'.'}
              </p>
              {settings.data?.updated_by && (
                <p className="text-xs text-fg-muted mt-1">
                  Last changed by {settings.data.updated_by} at{' '}
                  {new Date(settings.data.updated_at).toLocaleString()}.
                </p>
              )}
              {!enabled && (
                <div className="mt-3 flex items-start gap-2 text-xs text-warning bg-warning/5 border border-warning/20 rounded-md px-3 py-2 max-w-prose">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Enabling the playground exposes a try-it form that calls
                    your own API with the current admin session. In a busy
                    production deployment, repeated heavy calls (e.g.{' '}
                    <code className="mx-1">/api/v1/search</code>) can starve
                    real traffic. Leave it off unless you actively need it.
                  </span>
                </div>
              )}
            </div>
            <Button
              variant={enabled ? 'secondary' : 'primary'}
              size="md"
              disabled={busy}
              onClick={() => setEnabled(!enabled)}
            >
              {enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Card 2 — Spec download (always available when enabled, mirrors
          the docs.orbitalreg.io public Redoc but pinned to the running
          binary's version). */}
      {enabled && (
        <Card className="mb-6">
          <CardHeader
            title="OpenAPI spec"
            description="The same spec the docs site renders, pinned to this deployment's running version."
            actions={
              <a
                href="/api/admin/api-playground/openapi.yaml"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
              >
                <Download className="h-3.5 w-3.5" />
                openapi.yaml
              </a>
            }
          />
          <CardBody>
            <p className="text-sm text-fg-muted">
              {operations.data
                ? `Version ${operations.data.version} — ${ops.length} operations across ${tags.length} tags.`
                : 'Loading operations…'}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Card 3 — Operation browser */}
      {enabled && (
        <Card>
          <CardHeader
            title="Operations"
            description="Pick an operation to expand its details and try it against the live API."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => operations.mutate()}
                disabled={operations.isLoading}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            }
          />
          <CardBody>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative flex-1 min-w-[16rem]">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
                <Input
                  className="pl-8"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by path, operationId, or summary…"
                />
              </div>
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="h-10 rounded-md border border-border bg-bg-elevated px-3 text-sm text-fg"
              >
                <option value="">All tags</option>
                {tags.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {operations.isLoading && (
              <p className="text-sm text-fg-muted">Loading operations…</p>
            )}
            {operations.error && (
              <p className="text-sm text-danger">
                Failed to load operations: {String(operations.error)}
              </p>
            )}
            {!operations.isLoading && filtered.length === 0 && (
              <p className="text-sm text-fg-muted">
                No operations match the current filter.
              </p>
            )}

            <div className="space-y-6">
              {grouped.map(([tag, list]) => (
                <div key={tag} className="space-y-2">
                  <h4 className="text-sm font-semibold text-nebula">{tag}</h4>
                  <div className="space-y-1.5">
                    {list.map((op) => (
                      <OperationRow
                        key={`${op.method} ${op.path}`}
                        op={op}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </AdminShell>
  );
}
